import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
// @ts-expect-error The existing Redux actions are JavaScript.
import { logout as logoutAction } from "../../store/actions/auth";
// @ts-expect-error The existing modal is JavaScript.
import EmptyModal from "../../components/modals/EmptyModal";
// @ts-expect-error The existing error content is JavaScript.
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
// @ts-expect-error The existing date filter is JavaScript.
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
// @ts-expect-error The existing select wrapper is JavaScript.
import SelectMenu from "../../components/controls/SelectMenu";
// @ts-expect-error The existing button spinner is JavaScript.
import ButtonSpinner from "../../components/controls/ButtonSpinner";
import QaReceivingOptions from "../../components/forms/qaReports/receiving/QaReceivingOptions";
import ProductReportFilter from "../../components/forms/qaReports/ProductReportFilter";
import ProductionDetailsFilter from "../../components/forms/qaReports/productionDetails/ProductionDetailsFilter";
import ProductionSummaryFilter from "../../components/forms/qaReports/productionSummary/ProductionSummaryFilter";
import RecentReports from "../../components/forms/qaReports/RecentReports";
import { useQaReports } from "../../viewmodels/qaReports/useQaReports";
import { REPORTS } from "../../viewmodels/qaReports/qaReportConfig";
import type { FilterDate, ReportChoice, SelectionEvent } from "../../viewmodels/qaReports/qaReportTypes";

export function QaReportsScreen({ logout }: { logout: () => void }) {
  const model = useQaReports(logout);
  const { filters, setFilters, reportType, loading, initialize } = model;
  const definition = reportType.value === -1 ? null : REPORTS[reportType.value];
  const filterProps = { filters, setFilters, logout, disabled: loading };

  useEffect(() => { void initialize(); }, [initialize]);

  function renderReportFields() {
    switch (reportType.value) {
      case 1:
      case 2:
        return <QaReceivingOptions {...filterProps} categories={model.categories} suppliers={model.suppliers} />;
      case 4:
      case 8:
        return <ProductReportFilter {...filterProps} />;
      case 9:
        return <ProductionDetailsFilter {...filterProps} />;
      case 10:
        return <ProductionSummaryFilter {...filterProps} categories={model.categories} classes={model.classes} />;
      default:
        return null;
    }
  }

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <fieldset className="min-w-0 sm:flex-auto space-y-5" disabled={loading}>
              <h1 className="text-base font-semibold leading-6 text-gray-900">Generate reports</h1>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                <div className="mt-2 sm:col-span-2">
                  <SelectMenu
                    title="Report type"
                    disabled={loading}
                    handleInputChange={(event: SelectionEvent<ReportChoice>) => model.selectReport(event.target.value)}
                    name="reportType"
                    options={model.qaReportOptions}
                    value={reportType}
                  />
                </div>
                <div className={definition?.dates === "none" ? "hidden" : "sm:col-span-2"}>
                  <DateRangeFilter
                    hideTitle
                    stackOnMobile
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    onChange={(name: "startDate" | "endDate", value: FilterDate) => setFilters((current) => ({ ...current, [name]: value }))}
                    resetFilter={() => setFilters((current) => ({ ...current, startDate: null, endDate: null }))}
                    maxDate={new Date()}
                    showOneDate={definition?.dates === "single"}
                  />
                </div>
                <div className="mt-auto flex w-full flex-col gap-2 sm:col-span-2 sm:w-auto sm:flex-row">
                  <button
                    className="w-full sm:w-auto rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => void model.printSelectedReport("pdf")}
                    disabled={loading}
                    aria-busy={model.activeFormat === "pdf"}
                  >
                    <ButtonSpinner spin={model.activeFormat === "pdf"} text={model.activeFormat === "pdf" ? "Generating…" : "Download"} />
                  </button>
                  {definition?.supportsExcel && (
                    <button
                      className="w-full sm:w-auto rounded-md border border-main-purple px-3 py-2 text-sm font-semibold main-purple shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={() => void model.printSelectedReport("xlsx")}
                      disabled={loading}
                      aria-busy={model.activeFormat === "xlsx"}
                    >
                      <span className="inline-flex items-center gap-2">
                        {model.activeFormat === "xlsx" && <span aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none rounded-full border-2 border-current border-t-transparent" />}
                        {model.activeFormat === "xlsx" ? "Generating…" : "Download Excel"}
                      </span>
                    </button>
                  )}
                </div>
              </div>
              {renderReportFields()}
            </fieldset>
          </div>
        </div>
      </div>
      <RecentReports
        reports={model.recentReports}
        loading={model.recentReportsLoading}
        error={model.recentReportsError}
        updateError={model.recentReportsUpdateError}
        onDownload={model.downloadReport}
        onRetry={model.retryReport}
        retryingIds={model.retryingIds}
      />
      <EmptyModal
        childComponent={model.error ? <ModelErrorComponent title={model.error.title} subtitle={model.error.subtitle} setOpen={model.clearError} /> : null}
        open={model.error !== null}
        setOpen={model.clearError}
      />
    </div>
  );
}

export default function QaReports() {
  const dispatch = useDispatch();
  const logout = useCallback(() => { dispatch(logoutAction()); }, [dispatch]);
  return <QaReportsScreen logout={logout} />;
}
